<template>
  <div class="control-bar py-2 d-flex align-center">
    <div class="d-flex align-center pl-3" v-if="!isSelfServe">
      <div class="mr-3" v-if="hasManyCampaigns">
        <span class="label">{{$t('controlbar_campaign')}}</span>
        <span class="value font-weight-bold pl-1">{{campaignName}}</span>
      </div>
      <div class="mr-3">
        <span class="label">{{$t('controlbar_products_affected')}}</span>
        <span class="value font-weight-bold pl-1">{{productCount}}</span>
      </div>
    </div>

    <div :class="{ 'pl-3' : isSelfServe }" v-if="editor.default_language">
      <span class="label">{{ $t('reference_language') }}: </span>
      <v-tooltip bottom>
        <template v-slot:activator="{ on, attrs }">
          <span
            @click="openLanguageModal"
            v-bind="attrs"
            v-on="on"
            class="value font-weight-bold pl-1 pointer"
          >
            {{ $t(`languages_${editor.default_language}` )}}
          </span>
        </template>
        <span>{{ $t('translations_change_reference_language') }}</span>
      </v-tooltip>
    </div>

    <v-spacer></v-spacer>

    <div class="d-flex align-center segmented-button-ctn" v-if="!hideUpdateButton">

      <info-tooltip v-if="hasPublishedVersion && hasUnpublishedChanges && !publishing && !saving" class="more-tooltip ml-3" :compact="true" color="#f9ad61" position="left" :content="$t('controlbar_changes_saved')"></info-tooltip>
      <!-- <div class="hint control-bar-hint font-weight-bold" >
        All changes saved but not yet synced to the published version.
      </div>

      <div class="hint control-bar-hint font-weight-bold" >
        <span >Your redirection changes are not updated yet.</span>
      </div> -->

      <v-btn color="primary" small class="font-weight-black update-btn" :class="{ 'has-addon' : hasPublishedVersion && hasUnpublishedChanges }" :disabled="publishing || saving || (!hasUnpublishedChanges && !needToUpdateRedirection)" @click="publish">
        <span v-if="saving">{{$t('saving')}}</span>
        <span v-if="publishing && !hasPublishedVersion">{{$t('publishing')}}</span>
        <span v-if="publishing && hasPublishedVersion">{{$t('updating')}}</span>
        <span v-if="!publishing && !saving && !hasPublishedVersion">{{$t('publish')}}</span>
        <span v-if="!publishing && !saving && hasPublishedVersion">{{$t('update')}}</span>
      </v-btn>

      <v-menu v-if="hasPublishedVersion && hasUnpublishedChanges" bottom offset-y nudge-left="190" nudge-bottom="1">
        <template v-slot:activator="{ on }">
          <v-btn class="drop-down-btn ma-0 pa-0 mr-4" small :disabled="publishing || saving" v-on="on">
            <v-icon>$drop-down</v-icon>
          </v-btn>
        </template>
        <v-list class="ma-0 pa-0">
          <v-list-item class="pt-0 pb-0"
            @click="openRestorePopup"
          >
            <v-list-item-title class="font-weight-light text-caption">{{$t('controlbar_restore_last_published')}}</v-list-item-title>
          </v-list-item>
        </v-list>
      </v-menu>
    </div>

    <v-dialog v-model="restoreModal" width="500">
      <v-card>
        <v-card-title class="headline">{{$t('controlbar_restore_last_published_question')}}</v-card-title>
        <v-card-text class="py-4">
          {{$t('controlbar_restore_warning')}}
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn @click="restoreModal = false" text>{{$t('cancel')}}</v-btn>
          <v-btn @click="restorePublishedVersion" color="red" :disabled="restoring" text>{{$t('confirm')}}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <language-edit-modal
      v-model="languageModal"
      :show-go-to-translations="true"
    />
  </div>
</template>

<script>

import { mapGetters } from 'vuex'
import InfoTooltip from './utils/InfoTooltip.vue'
import LanguageEditModal from './utils/LanguageEditModal.vue'
import amplitude from 'amplitude-js'

export default {
  components: { InfoTooltip, LanguageEditModal },
  name: 'ControlBar',
  props: ['hideUpdateButton'],
  computed: {
    ...mapGetters(['initialized', 'publishing', 'saving', 'published', 'hasUnpublishedChanges', 'needToUpdateRedirection', 'hasPublishedVersion', 'campaignName', 'productCount', 'hasManyCampaigns', 'defaultLanguage', 'isSelfServe', 'editor'])
  },
  data () {
    return {
      restoring: false,
      restoreModal: false,
      languageModal: false
    }
  },
  methods: {
    async restorePublishedVersion () {
      this.restoring = true
      await this.$store.dispatch('restorePublishedVersion', { config: this.$route.params.id })
      this.$store.dispatch('showSnackBar', { message: 'Landing page successfully restored', duration: 5000 })
      this.restoring = false
      this.restoreModal = false
    },

    openRestorePopup () {
      this.restoreModal = true
    },

    openLanguageModal () {
      this.languageModal = true
    },

    async publish () {
      if (this.needToUpdateRedirection) {
        await this.$store.dispatch('saveRedirectionSettings')
        parent.postMessage({ type: 'editor.update-redirection' }, '*')
      }
      await this.$store.dispatch('remotePublish', this.published.reviewed_languages?.includes(this.defaultLanguage) ? [] : [this.defaultLanguage])
      if (this.needToUpdateRedirection === 'product' || this.needToUpdateRedirection === 'url') {
        this.$store.dispatch('showSnackBar', { message: 'Redirection settings updated!', duration: 5000 })
      } else {
        this.$store.dispatch('showSnackBar', { message: 'Landing page updated! 🚀🚀🚀', duration: 5000 })
      }
      amplitude.getInstance().logEvent('Page Update')
    }
  },
  created () {
    this.$store.dispatch('loadCampaignCount')
  }
}

</script>

<style lang="sass" scoped>

@import '@/variables.sass'

.pointer
  cursor: pointer

.control-bar
  min-height: 52px
  font-size: 12px
  background-color: #f1f1f1
  text-transform: uppercase

.label
  color: #777777

.value
  color: rgba(var(--primary))
  &.pointer
    cursor: pointer
    &:hover
      text-decoration: underline

.segmented-button-ctn
  margin-right: 40px
  @include lg
    margin-right: 0px
  position: relative
  .update-btn
    margin-right: 15px
    &.has-addon
      border-top-right-radius: 0px !important
      border-bottom-right-radius: 0px !important
      margin-right: 0px
  .hide
    display: none
  .drop-down-btn
    min-width: 0px
    width: 30px
    background-color: #333 !important
    bottom: 0
    border-top-left-radius: 0px !important
    border-bottom-left-radius: 0px !important
    margin: 0
    padding: 0
    top: 0
    right: 0

.control-bar-hint
  opacity: 0
  &.show
    opacity: 1

</style>
